/**
 * Tax Information Form Component
 * Second step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { TaxInformation, ValidationStatus, registrationService } from '../../services/registration.service';

interface TaxInformationFormProps {
  data?: TaxInformation;
  onSave: (data: TaxInformation) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  saving: boolean;
}

export function TaxInformationForm({ data, onSave, onNext, onPrevious, saving }: TaxInformationFormProps) {
  const [formData, setFormData] = useState<TaxInformation>({
    gstin: '',
    pan: '',
    cin: '',
    udyamNumber: '',
    gstinValidationStatus: 'pending',
    panValidationStatus: 'pending',
    cinValidationStatus: 'pending'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validating, setValidating] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.gstin.trim()) {
      newErrors.gstin = 'GSTIN is required';
    } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
      newErrors.gstin = 'Invalid GSTIN format';
    }

    if (!formData.pan.trim()) {
      newErrors.pan = 'PAN is required';
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)) {
      newErrors.pan = 'Invalid PAN format';
    }

    // CIN is optional but validate format if provided
    if (formData.cin && !/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(formData.cin)) {
      newErrors.cin = 'Invalid CIN format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValidateGSTIN = async () => {
    if (!formData.gstin.trim()) return;

    setValidating(prev => ({ ...prev, gstin: true }));
    try {
      const result = await registrationService.validateGSTIN(formData.gstin);
      setFormData(prev => ({
        ...prev,
        gstinValidationStatus: result.status
      }));
    } catch (error) {
      console.error('GSTIN validation error:', error);
    } finally {
      setValidating(prev => ({ ...prev, gstin: false }));
    }
  };

  const handleValidatePAN = async () => {
    if (!formData.pan.trim()) return;

    setValidating(prev => ({ ...prev, pan: true }));
    try {
      const result = await registrationService.validatePAN(formData.pan, formData.gstin);
      setFormData(prev => ({
        ...prev,
        panValidationStatus: result.status
      }));
    } catch (error) {
      console.error('PAN validation error:', error);
    } finally {
      setValidating(prev => ({ ...prev, pan: false }));
    }
  };

  const handleValidateCIN = async () => {
    if (!formData.cin?.trim()) return;

    setValidating(prev => ({ ...prev, cin: true }));
    try {
      const result = await registrationService.validateCIN(formData.cin);
      setFormData(prev => ({
        ...prev,
        cinValidationStatus: result.status
      }));
    } catch (error) {
      console.error('CIN validation error:', error);
    } finally {
      setValidating(prev => ({ ...prev, cin: false }));
    }
  };

  const getValidationStatusColor = (status: ValidationStatus) => {
    switch (status) {
      case 'verified':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'manual_review_required':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getValidationStatusText = (status: ValidationStatus) => {
    switch (status) {
      case 'verified':
        return 'Verified ✓';
      case 'failed':
        return 'Failed ✗';
      case 'manual_review_required':
        return 'Manual Review Required';
      default:
        return 'Pending';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GSTIN */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            GSTIN (Goods and Services Tax Identification Number) *
          </label>
          <div className="flex gap-2">
            <Input
              value={formData.gstin}
              onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              error={errors.gstin}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleValidateGSTIN}
              disabled={validating.gstin || !formData.gstin.trim()}
              className="px-4"
            >
              {validating.gstin ? 'Validating...' : 'Validate'}
            </Button>
          </div>
          <div className={`mt-1 text-sm ${getValidationStatusColor(formData.gstinValidationStatus)}`}>
            {getValidationStatusText(formData.gstinValidationStatus)}
          </div>
        </div>

        {/* PAN */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            PAN (Permanent Account Number) *
          </label>
          <div className="flex gap-2">
            <Input
              value={formData.pan}
              onChange={(e) => setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
              placeholder="AAAAA0000A"
              maxLength={10}
              error={errors.pan}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleValidatePAN}
              disabled={validating.pan || !formData.pan.trim()}
              className="px-4"
            >
              {validating.pan ? 'Validating...' : 'Validate'}
            </Button>
          </div>
          <div className={`mt-1 text-sm ${getValidationStatusColor(formData.panValidationStatus)}`}>
            {getValidationStatusText(formData.panValidationStatus)}
          </div>
        </div>

        {/* CIN */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CIN (Corporate Identification Number)
            <span className="text-gray-500 text-xs ml-1">(For Private/Public Limited Companies)</span>
          </label>
          <div className="flex gap-2">
            <Input
              value={formData.cin || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, cin: e.target.value.toUpperCase() }))}
              placeholder="U12345MH2020PTC123456"
              maxLength={21}
              error={errors.cin}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleValidateCIN}
              disabled={validating.cin || !formData.cin?.trim()}
              className="px-4"
            >
              {validating.cin ? 'Validating...' : 'Validate'}
            </Button>
          </div>
          {formData.cin && (
            <div className={`mt-1 text-sm ${getValidationStatusColor(formData.cinValidationStatus || 'pending')}`}>
              {getValidationStatusText(formData.cinValidationStatus || 'pending')}
            </div>
          )}
        </div>

        {/* UDYAM Number */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            UDYAM Registration Number
            <span className="text-gray-500 text-xs ml-1">(For MSMEs)</span>
          </label>
          <Input
            value={formData.udyamNumber || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, udyamNumber: e.target.value }))}
            placeholder="UDYAM-XX-00-0000000"
          />
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Validation Information</h3>
            <div className="text-sm text-blue-700 mt-1">
              <p>• We validate government registration numbers automatically when possible</p>
              <p>• If automatic validation fails, your application will be reviewed manually</p>
              <p>• PAN should match the PAN embedded in your GSTIN</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}